'use client';

import {
  ConditionalWrapper,
  Box,
  Link,
  DynamicYield,
  Stack,
  useTrackCampaignOnLoad,
  Tooltip,
} from '@packages/shared';
import { useLoginState } from '@packages/utilities';
import { useEffect, type FC } from 'react';
import { useFilterTimeControl } from '@packages/shared/src/utils/filterTimeControl';
import { useUspData } from './useUspData';
import type { UspItem } from './types';

const dynamicIDs = [
  'usp_replace-first-usp',
  'usp_replace-second-usp',
  'usp_replace-third-usp',
  'usp_replace-forth-usp',
];

interface USPLinkProps {
  dynamicID: string;
  link: UspItem;
}

const USPLink: FC<USPLinkProps> = ({ dynamicID, link }) => {
  const {
    translatable_url: url,
    translatable_title: text,
    promotion_item: tooltipData,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
  } = link;
  const { trackClick, trackDisplay } = useTrackCampaignOnLoad(link, link.translatable_url);
  const filterTimeControl = useFilterTimeControl();

  const tooltipContent =
    tooltipData?.find((item) =>
      filterTimeControl({
        field_valid_from: item.field_promotion_valid_from_date,
        field_valid_until: item.field_promotion_valid_until_date,
      }),
    )?.field_promotion_feedback_descrip ?? null;

  useEffect(() => {
    trackDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <DynamicYield dataDyId={dynamicID} />
      <Link
        href={url}
        data-testid="usp-link"
        noLinkStyle
        sx={{
          display: 'flex',
          pt: '0.4rem',
          pb: '0.4rem',
          typography: 'body1',
          fontSize: '0.875rem',
          textAlign: 'center',
        }}
        onClick={() => trackClick()}
      >
        <ConditionalWrapper
          condition={!!(tooltipContent && !!tooltipActivation)}
          wrapper={(ch) => (
            <Tooltip variant="inline" content={tooltipContent} positioning={tooltipPositioning}>
              {ch}
            </Tooltip>
          )}
        >
          <Box
            sx={{
              lineHeight: '1.5rem',
            }}
            component="span"
          >
            {text}
          </Box>
        </ConditionalWrapper>
      </Link>
    </Box>
  );
};

/**
 * USP are Links under Desktop Navigation
 * It will render links from Array
 * Links are hard coded and should be changed in Config
 */
export const Usp = () => {
  const { loggedIn } = useLoginState();
  const { data: uspData } = useUspData();
  const linkData = uspData?.find((item) => item.title === (loggedIn ? 'Angemeldet' : 'Abgemeldet'));

  if (!linkData) {
    return null;
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{
        maxWidth: 'xl',
        borderBottom: '0.0625rem solid black',
        borderBottomColor: 'grey.light',
      }}
      spacing={2}
    >
      {linkData.children.map((link, index) => (
        <USPLink key={dynamicIDs[index]} dynamicID={dynamicIDs[index]} link={link} />
      ))}
    </Stack>
  );
};
